import {
    Button,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Link,
    Outlet,
    useLocation,
    useNavigate,
    useOutletContext,
} from 'react-router';
import {
    RequestMethod,
    RequestStatus,
    ServerRequestState,
    useServerErrorEffect,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import {
    Add,
    CheckCircleOutlined,
    Description,
    PictureAsPdf,
    QrCode2,
    RemoveCircleOutlineOutlined,
} from '@mui/icons-material';
import { useState } from 'react';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import {
    EmptyResults,
    FetchError,
    ResultLimitAlert,
} from '../shared-mui-components/ListMessages.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { BackendRequestErrorMessage } from 'dg-web-shared/common/components/material-ui/BackendRequestErrorMessage.tsx';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { FiltersDropdown } from '../parkingabo-users/OperatorParkingaboUsersFilterList.tsx';
import { setSearchURL } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { FilterBar } from '../shared-mui-components/filter/FiltersBox.tsx';
import { useRecodeQrCodesFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';

export interface RecodeQrCodesResponse {
    allowRecodeQrCodesGeneration: boolean;
    recodeQrCodeStacks: RecodeQrCodeStack[];
    eof: boolean;
}

enum RecodeQrCodeStatus {
    VALID = 'VALID',
    EXPIRED = 'EXPIRED',
}

interface RecodeQrCodeStack {
    stackId: string;
    recodeConditionDescription: Message;
    name: string;
    totalCount: number;
    usedCount: number;
    validCount: number;
    createdAt: string;
    validTo: string;
    status: RecodeQrCodeStatus;
}

export function CloudConnectorRecodeQrCodes() {
    const navigate = useNavigate();
    const location = useLocation();
    const {
        filterConfig,
        activeFilters,
        listState,
        refetchList,
        filterConfigState,
        searchText,
    } = useRecodeQrCodesFilterConfiguration();

    const [showGenerateQrButton, setShowGenerateQrButton] = useState(false);
    useServerSuccessEffect(listState, data =>
        setShowGenerateQrButton(data.allowRecodeQrCodesGeneration),
    );

    return (
        <MainContent>
            <HeaderWithButtons
                title={
                    <Localized
                        de="Rabattkarten"
                        fr="Cartes de rabais"
                        it="Carte ribasso"
                        en="Discount cards"
                    />
                }
                icon={QrCode2}
                headerButtons={
                    <Stack
                        direction="row"
                        spacing={3}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        {showGenerateQrButton && (
                            <Link to={'add'}>
                                <Button
                                    variant="contained"
                                    startIcon={<Add />}
                                    color="primary"
                                >
                                    <Localized
                                        de="Stapel generieren"
                                        fr="Générer set"
                                        it="Genera set"
                                        en="Generate stack"
                                    />
                                </Button>
                            </Link>
                        )}

                        <Button
                            variant="outlined"
                            onClick={() => navigate('../statement')}
                        >
                            <Localized
                                de="Schliessen"
                                fr="Fermer"
                                it="Chiudi"
                                en="Close"
                            />
                        </Button>
                    </Stack>
                }
            />
            <FilterBar>
                <FiltersDropdown
                    activeFilters={activeFilters}
                    filterConfig={filterConfig}
                    onFiltersStateChange={activeFilters =>
                        setSearchURL(
                            navigate,
                            location,
                            searchText,
                            activeFilters,
                        )
                    }
                    pending={
                        filterConfigState.status === RequestStatus.PENDING &&
                        Object.keys(filterConfig).length === 0
                    }
                />
            </FilterBar>
            <RecodeQrCodesStackList
                recodeQrCodesState={listState}
                refetchRecodeQrCodes={refetchList}
            />
            <RecodeQrCodesOutlet context={{ refetchList }} />
        </MainContent>
    );
}

export function useRecodeQrCodesOutlet() {
    return useOutletContext<RecodeQrCodesOutletContext>();
}

function RecodeQrCodesOutlet({
    context,
}: {
    context: RecodeQrCodesOutletContext;
}) {
    return <Outlet context={context} />;
}

interface RecodeQrCodesOutletContext {
    refetchList: () => void;
}

function RecodeQrCodesStackList({
    recodeQrCodesState,
    refetchRecodeQrCodes,
}: {
    recodeQrCodesState: ServerRequestState<RecodeQrCodesResponse, null>;
    refetchRecodeQrCodes: () => void;
}) {
    return (
        <OperatorAsyncLoadedSection
            requestState={recodeQrCodesState}
            pendingLoaderPreset={LoadingSpinnerPresets.FillAllSpaceAndCenter}
            render={response => (
                <RecodeQrCodesStackListContent
                    response={response}
                    refetchRecodeQrCodes={refetchRecodeQrCodes}
                />
            )}
            renderError={() => (
                <FetchError refetchList={refetchRecodeQrCodes} />
            )}
        />
    );
}

function RecodeQrCodesStackListContent({
    response,
    refetchRecodeQrCodes,
}: {
    response: RecodeQrCodesResponse;
    refetchRecodeQrCodes: () => void;
}) {
    const { operatorExternalId } = useOperatorContext();
    const { language } = useLanguage();

    const [deleteModalContent, setDeleteModalContent] =
        useState<RecodeQrCodeStack | null>(null);

    const [deleteStackRequestState, deleteStack] = useServerWrite<
        { qrCodeStackId: string },
        null,
        ValidationData
    >(({ qrCodeStackId }) => ({
        url: `/ui-api/operator-account/cloud-connector/recode/qr-codes/${qrCodeStackId}`,
        method: RequestMethod.DELETE,
    }));

    useServerSuccessEffect(deleteStackRequestState, () => {
        refetchRecodeQrCodes();
        setDeleteModalContent(null);
    });

    useServerErrorEffect(deleteStackRequestState, () => {
        setDeleteModalContent(null);
    });

    const isEmpty = response.recodeQrCodeStacks.length === 0;

    if (isEmpty) {
        return <EmptyResults />;
    }

    return (
        <>
            <BackendRequestErrorMessage
                requestState={deleteStackRequestState}
            />
            <TableContainer>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Localized
                                    de="Status"
                                    fr="Status"
                                    it="Status"
                                    en="Status"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Bedingung"
                                    fr="Condition"
                                    it="Condizione"
                                    en="Condition"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Stapel"
                                    fr="Set"
                                    it="Set"
                                    en="Stack"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Total"
                                    fr="Totale"
                                    it="Totale"
                                    en="Total"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Gültige"
                                    fr="Valides"
                                    it="Validi"
                                    en="Valid"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Gebrauchte"
                                    fr="Utilisés"
                                    it="Usati"
                                    en="Used"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Erstellungsdatum"
                                    fr="Date de création"
                                    it="Data di creazione"
                                    en="Creation date"
                                />
                            </TableCell>
                            <TableCell>
                                <Localized
                                    de="Ablaufdatum"
                                    fr="Date d'échéance"
                                    it="Data scadenza"
                                    en="Expiration date"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <Localized
                                    de="Aktionen"
                                    fr="Actions"
                                    it="Azioni"
                                    en="Actions"
                                />
                            </TableCell>
                            <TableCell>PDF</TableCell>
                            <TableCell>EXCEL</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {response.recodeQrCodeStacks.map(
                            (qrCodeStack: RecodeQrCodeStack) => {
                                return (
                                    <TableRow key={qrCodeStack.stackId}>
                                        <TableCell>
                                            <IconForQrCodeStackStatus
                                                qrCodeStack={qrCodeStack}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                {...qrCodeStack.recodeConditionDescription}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {qrCodeStack.name}
                                        </TableCell>
                                        <TableCell>
                                            {qrCodeStack.totalCount}
                                        </TableCell>
                                        <TableCell>
                                            {getValidQrCodeCount(qrCodeStack)}
                                        </TableCell>
                                        <TableCell>
                                            {qrCodeStack.usedCount
                                                ? qrCodeStack.usedCount
                                                : EmDash}
                                        </TableCell>
                                        <TableCell>
                                            {Formatter.dayMonthYear(
                                                DateTime.fromISO(
                                                    qrCodeStack.createdAt,
                                                ),
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {Formatter.dayMonthYear(
                                                DateTime.fromISO(
                                                    qrCodeStack.validTo,
                                                ),
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {qrCodeStack.status ===
                                                RecodeQrCodeStatus.VALID &&
                                                qrCodeStack.validCount && (
                                                    <Tooltip
                                                        title={
                                                            <Localized
                                                                de="gültige QR-Codes löschen"
                                                                fr="supprimer les codes QR valides"
                                                                it="elimina codici QR validi"
                                                                en="delete valid QR codes"
                                                            />
                                                        }
                                                        placement="right"
                                                    >
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() =>
                                                                setDeleteModalContent(
                                                                    qrCodeStack,
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                        </TableCell>
                                        <TableCell>
                                            <a
                                                target="_blank"
                                                href={urlWithOperatorExternalId(
                                                    `/ui-api/operator-account/cloud-connector/recode/qr-codes/${qrCodeStack.stackId}/pdf`,
                                                    operatorExternalId,
                                                )}
                                                rel="noreferrer"
                                            >
                                                <PictureAsPdf
                                                    sx={{
                                                        color: theme =>
                                                            theme.palette
                                                                .primary.main,
                                                        '&:hover': {
                                                            color: theme =>
                                                                theme.palette
                                                                    .blue
                                                                    .electric,
                                                        },
                                                    }}
                                                />
                                            </a>
                                        </TableCell>
                                        <TableCell>
                                            {qrCodeStack.status ===
                                                RecodeQrCodeStatus.EXPIRED && (
                                                <a
                                                    target="_blank"
                                                    href={urlWithOperatorExternalId(
                                                        `/ui-api/operator-account/cloud-connector/recode/qr-codes/${qrCodeStack.stackId}/export?lang=${language}`,
                                                        operatorExternalId,
                                                    )}
                                                    rel="noreferrer"
                                                >
                                                    <Description
                                                        sx={{
                                                            color: theme =>
                                                                theme.palette
                                                                    .primary
                                                                    .main,
                                                            '&:hover': {
                                                                color: theme =>
                                                                    theme
                                                                        .palette
                                                                        .blue
                                                                        .electric,
                                                            },
                                                        }}
                                                    />
                                                </a>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            },
                        )}
                    </TableBody>
                </Table>
                <ResultLimitAlert
                    eof={response.eof}
                    numberOfResults={response.recodeQrCodeStacks.length}
                    hasExport={true}
                />
                <ConfirmationDialog
                    open={Boolean(deleteModalContent)}
                    title={
                        <Localized
                            de="QR-Codes löschen"
                            fr="Supprimer les codes QR"
                            it="Eliminare codici QR"
                            en="Delete QR codes"
                        />
                    }
                    confirmLabel={
                        <Localized
                            de="Löschen"
                            fr="Effacher"
                            it="Elimina"
                            en="Delete"
                        />
                    }
                    confirmDisabled={
                        deleteStackRequestState.status === RequestStatus.PENDING
                    }
                    onConfirm={() =>
                        deleteStack({
                            qrCodeStackId: deleteModalContent!.stackId,
                        })
                    }
                    onCancel={() => setDeleteModalContent(null)}
                    dialogVersion={ConfirmationDialogVersions.SOFT_WARNING}
                >
                    <>
                        <p>
                            <Localized
                                de={`Möchten Sie die ${deleteModalContent?.validCount} gültigen QR-Codes aus dem Stapel ${deleteModalContent?.name} löschen?`}
                                fr={`Voulez-vous supprimer les ${deleteModalContent?.validCount} codes QR valides du set ${deleteModalContent?.name}?`}
                                it={`Vuole eliminare i ${deleteModalContent?.validCount} codici QR validi del set ${deleteModalContent?.name}?`}
                                en={`Do you want to delete the ${deleteModalContent?.validCount} valid QR codes from the ${deleteModalContent?.name} stack?`}
                            />
                        </p>
                        <p>
                            <Localized
                                de="ACHTUNG: diese Aktion kann nicht rückgängig gemacht werden."
                                fr="ATTENTION: cette opération est irréversible."
                                it="ATTENZIONE: questa operazione non sarà reversibile."
                                en="WARNING: this operation cannot be reversed."
                            />
                        </p>
                    </>
                </ConfirmationDialog>
            </TableContainer>
        </>
    );
}
function getValidQrCodeCount(qrCodeStack: RecodeQrCodeStack) {
    switch (qrCodeStack.status) {
        case RecodeQrCodeStatus.VALID:
            return qrCodeStack.validCount;
        case RecodeQrCodeStatus.EXPIRED:
            return EmDash;
    }
}

function IconForQrCodeStackStatus({
    qrCodeStack,
}: {
    qrCodeStack: RecodeQrCodeStack;
}) {
    switch (qrCodeStack.status) {
        case RecodeQrCodeStatus.VALID:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="gültig"
                            fr="valables"
                            it="validi"
                            en="valid"
                        />
                    }
                    placement="right"
                >
                    <CheckCircleOutlined color="success" />
                </Tooltip>
            );
        case RecodeQrCodeStatus.EXPIRED:
            return (
                <Tooltip
                    title={
                        <Localized
                            de="abgelaufen"
                            fr="expirés"
                            it="scaduti"
                            en="expired"
                        />
                    }
                    placement="right"
                >
                    <RemoveCircleOutlineOutlined color="disabled" />
                </Tooltip>
            );
    }
}
